/* eslint-disable @typescript-eslint/no-shadow */
import type { Proxy } from '#~/agent/index.js';
import type { DeviceProps } from '#~/device/components/index.js';
import type { DeviceSetting } from '#~/device/settings/index.js';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control, DeviceRegistryCommand } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type {
  DeviceRegistryCommandKey,
  DeviceRegistryInterfaceItem
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryInterfaceItemControlType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import type { InterfaceCondition } from './conditions.js';
import { DeviceSettingControl } from './controls/index.js';
import { sortWeightedItems } from './wiring.js';

/** @internal */
export type ControlsProps = DeviceProps &
  HtmlAttributes & {
    agent: T_Maybe<Proxy>;
    commandMap: Record<number, DeviceRegistryCommand>;
    isOffline: boolean;
    conditionsMap: Record<number, InterfaceCondition>;
    defaultsMap: Record<number, number>;
    deviceSettings: Record<number, DeviceSetting>;
    items: Record<string, DeviceRegistryInterfaceItem>;
    nestedPassList?: DeviceRegistryCommandKey[];
    onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
    onControlClick?: (controlData: {
      label: string;
      controlType: DeviceRegistryInterfaceItemControlType;
      key: DeviceRegistryCommandKey;
      value?: DeviceRegistryCommandFunctionType;
    }) => void;
  };

/** `<Controls>` component. */
export const Controls = ({
  agent,
  className,
  commandMap,
  isOffline,
  conditionsMap,
  defaultsMap,
  deviceId,
  deviceSettings,
  items,
  nestedPassList,
  onMutation,
  onControlClick
}: ControlsProps): ReactElement => (
  <>
    {Object.entries(items)
      .sort(sortWeightedItems)
      .map(([uuid, item]) => {
        if (item.controlType === DeviceRegistryInterfaceItemControlType.MULTI_CONTROL) {
          const multiControlItems: Record<string, DeviceRegistryInterfaceItem> = {};

          const cmd = commandMap[item.key];
          const itemNestedPassList = Object.keys(cmd?.multi?.values ?? {}).map(Number);

          Object.entries(items).forEach(([key, item]) => {
            if (itemNestedPassList.includes(Number(key))) {
              multiControlItems[Number(key)] = item;
            }
          });

          return (
            <Controls
              agent={agent}
              className={className}
              commandMap={commandMap}
              conditionsMap={conditionsMap}
              isOffline={isOffline}
              defaultsMap={defaultsMap}
              deviceId={deviceId}
              deviceSettings={deviceSettings}
              items={multiControlItems}
              key={uuid}
              nestedPassList={itemNestedPassList}
              onMutation={onMutation}
              {...(onControlClick && { onControlClick })}
            />
          );
        }

        return (
          <DeviceSettingControl
            agent={agent}
            className={className}
            commandMap={commandMap}
            defaultsMap={defaultsMap}
            deviceId={deviceId}
            deviceSettings={deviceSettings}
            isDisabled={
              isOffline || (conditionsMap[item.key]?.isDisabled ?? false) || item?.readOnly || false
            }
            isHidden={
              ((item.nested && !nestedPassList?.includes(item.key)) ||
                conditionsMap[item.key]?.isHidden) ??
              false
            }
            item={item}
            key={uuid}
            onMutation={onMutation}
            {...(onControlClick && { onControlClick })}
          />
        );
      })}
  </>
);
